/**
 * Composants pour afficher le statut client basé sur la période
 * Design sobre et explicite, sans badges marketing
 */

import React from 'react';
import { Tooltip } from '@mui/material';
import { CustomerPeriodStatus, CUSTOMER_STATUS_CONFIG, PERIOD_OPTIONS, AnalysisPeriod } from './types';

interface CustomerStatusDisplayProps {
    status: CustomerPeriodStatus;
    size?: 'sm' | 'md' | 'lg';
    showTooltip?: boolean;
}

/**
 * Affiche le statut client (Nouveau, Existant actif, Inactif)
 */
export const CustomerStatusDisplay: React.FC<CustomerStatusDisplayProps> = ({
    status,
    size = 'md',
    showTooltip = true,
}) => {
    const config = CUSTOMER_STATUS_CONFIG[status];

    const sizeClasses = {
        sm: 'text-xs px-2 py-0.5',
        md: 'text-sm px-2.5 py-1',
        lg: 'text-base px-3 py-1.5',
    };

    const statusElement = (
        <span
            className={`
        inline-flex items-center font-medium rounded-md
        ${config.bgClass} ${config.textClass} ${config.borderClass}
        border transition-colors
        ${sizeClasses[size]}
      `}
        >
            {config.label}
        </span>
    );

    if (!showTooltip) {
        return statusElement;
    }

    return (
        <Tooltip
            title={config.description}
            arrow
            placement="top"
        >
            {statusElement}
        </Tooltip>
    );
};

interface CustomerStatusLegendProps {
    period: AnalysisPeriod;
}

/**
 * Légende explicative des statuts clients
 */
export const CustomerStatusLegend: React.FC<CustomerStatusLegendProps> = ({ period }) => {
    const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || `${period} jours`;

    return (
        <div className="bg-background-tertiary rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-foreground">
                    Définition des statuts
                </h4>
                <span className="text-xs text-foreground-secondary bg-surface px-2 py-1 rounded-md">
                    Basé sur les {periodLabel}
                </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Nouveau */}
                <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
                    <CustomerStatusDisplay status="new" size="sm" showTooltip={false} />
                    <p className="text-xs text-foreground-secondary flex-1">
                        Première commande dans la période
                    </p>
                </div>

                {/* Existant */}
                <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
                    <CustomerStatusDisplay status="existing" size="sm" showTooltip={false} />
                    <p className="text-xs text-foreground-secondary flex-1">
                        Client qui revient commander
                    </p>
                </div>

                {/* Inactif */}
                <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
                    <CustomerStatusDisplay status="inactive" size="sm" showTooltip={false} />
                    <p className="text-xs text-foreground-secondary flex-1">
                        Aucune commande sur la période
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CustomerStatusDisplay;
